import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { updateBoard, type UpdateBoardRequest } from '@/features/class/boardsApi';
import { useAuthStore, type RawAuthUser } from '@/store/authStore';

/**
 * Patch an edited board's fields (name / isPrivate) inside the auth-store's
 * `rawUser` payload so the Sidebar/profile (which read owned classes from the
 * login response, not React Query) reflect the edit immediately without a full
 * page refresh.
 *
 * Mirrors the helpers in `useCreateBoard.ts` / `useDeleteBoard.ts`: it keeps
 * both the legacy flat `board[]` and the modern `boardWithCategory[]` shapes
 * consistent.
 */
function patchBoardInRawUser(
  boardId: number | string,
  patch: { name: string; isPrivate: boolean },
): void {
  const { rawUser, setRawUser } = useAuthStore.getState();
  if (!rawUser) return;

  const targetId = String(boardId);
  const base: RawAuthUser = { ...rawUser };

  const applyPatch = (entry: unknown): unknown => {
    if (
      entry &&
      typeof entry === 'object' &&
      String((entry as { id?: number | string }).id) === targetId
    ) {
      return {
        ...(entry as Record<string, unknown>),
        name: patch.name,
        isPrivate: patch.isPrivate,
      };
    }
    return entry;
  };

  // 1) Legacy flat `board[]`.
  if (Array.isArray(base.board)) {
    base.board = (base.board as unknown[]).map(applyPatch);
  }

  // 2) Modern `boardWithCategory[]` — patch the row inside every group.
  if (Array.isArray(base.boardWithCategory)) {
    base.boardWithCategory = (base.boardWithCategory as unknown[]).map((group) => {
      if (
        group &&
        typeof group === 'object' &&
        Array.isArray((group as Record<string, unknown>).boards)
      ) {
        const typedGroup = group as Record<string, unknown>;
        return {
          ...typedGroup,
          boards: (typedGroup.boards as unknown[]).map(applyPatch),
        };
      }
      return group;
    });
  }

  setRawUser(base);
}

/**
 * PUT /midl/api/v3/board/<boardId> — updates an existing class (board).
 *
 * Wired from `ClassFormModal` so the "Save changes" submit persists edits to
 * the real backend. On success we also patch the auth-store `rawUser` so the
 * sidebar/profile (login-payload driven) and the React-Query joined-boards /
 * class-detail caches all reflect the edit without a hard reload.
 */
export function useUpdateBoardMutation() {
  const queryClient = useQueryClient();

  return useMutation<CreatedBoardResult, unknown, UpdateBoardRequest>({
    mutationKey: ['class', 'board', 'update'],
    mutationFn: (vars) => updateBoard(vars),
    onSuccess: (_data, vars) => {
      patchBoardInRawUser(vars.boardId, {
        name: vars.name.trim(),
        isPrivate: vars.isPrivate,
      });
      // Refresh server-driven sidebar/class lists + this class's detail cache.
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.class.detail(String(vars.boardId)),
      });
    },
  });
}

/** Result type alias kept local to avoid a broad import surface. */
type CreatedBoardResult = Awaited<ReturnType<typeof updateBoard>>;
